import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authorizationApi, platformApi } from '../../core/api/client';
import type { MembershipRow, RoleRow } from '../../core/api/types';
import { permissionApi, type ManageableProduct, type PermissionMatrix, type ResourceInput } from '../../core/authorization/api';
import {
  applyCell, applyColumn, applyPreset, diff, expandDependencies, flatten, toMask, toNames, toSaveBody,
  type GrantState, type MatrixNode, type Preset,
} from '../../core/authorization/permissionMatrix';
import { usePermissions } from '../../core/authorization/PermissionProvider';
import { PlatformApiError } from '../../core/http/errors';
import { EffectiveAccessDrawer } from './EffectiveAccessDrawer';
import { PermissionMatrixTable } from './PermissionMatrixTable';
import { ProductTabs } from './ProductTabs';
import { ResourceDialog } from './ResourceDialog';
import { RoleDialog, type RoleForm } from './RoleDialog';
import { RoleList, SearchIcon } from './RoleList';
import { grantsFor, initials, nodesFor, roleColor, titlesFor, type MatrixFilter } from './permissionUi';
import './RolesPermissionsPage.css';

const EMPTY_ROLE: RoleForm = { code: '', name: '', description: '', copyFromRoleId: '' };
const PRODUCT_KEY = 'permission-product';

const readPreference = () => { try { return localStorage.getItem(PRODUCT_KEY); } catch { return null; } };
const writePreference = (value: string) => { try { localStorage.setItem(PRODUCT_KEY, value); } catch { /* private mode */ } };

/**
 * Roles & Permissions (design: Architech-Systems/permission/05-ui-spec.md).
 *
 * Product tabs → role list → the visible menu tree of that product with
 * Read / Create / Update / Delete / Execute per menu. Anything not listed is an
 * open resource. All rule logic lives in core/authorization/permissionMatrix.ts.
 */
export function RolesPermissionsPage() {
  const { t } = useTranslation();
  const { can, hasScope } = usePermissions();
  const navigate = useNavigate();
  const { roleId: routeRole } = useParams();
  const [params, setParams] = useSearchParams();

  const [products, setProducts] = useState<ManageableProduct[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [roleId, setRoleId] = useState(params.get('role') ?? routeRole ?? '');
  const [product, setProduct] = useState((params.get('product') ?? readPreference() ?? 'PLATFORM').toUpperCase());
  const [matrix, setMatrix] = useState<PermissionMatrix | null>(null);
  const [original, setOriginal] = useState<GrantState>({});
  const [edited, setEdited] = useState<GrantState>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<MatrixFilter>('all');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [roleDialog, setRoleDialog] = useState<{ editing: boolean; initial: RoleForm } | null>(null);
  const [resourceDialog, setResourceDialog] = useState<{ id: string | null; initial: ResourceInput } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'role' | 'resource'; id: string; name: string } | null>(null);
  const [quickAnchor, setQuickAnchor] = useState<HTMLElement | null>(null);
  const [members, setMembers] = useState<MembershipRow[]>([]);
  const [checkUser, setCheckUser] = useState(false);
  const loadSequence = useRef(0);

  const canRead = can('platform.roles', 'read');
  const canManageResources = hasScope('platform.resources.manage');
  const nodes = useMemo(() => nodesFor(matrix?.tree ?? []), [matrix]);
  const titles = useMemo(() => titlesFor(matrix?.tree ?? []), [matrix]);
  const expanded = useMemo(() => expandDependencies(edited, matrix?.dependencies ?? [], matrix?.supportedById ?? {}), [edited, matrix]);
  const changes = useMemo(() => diff(original, edited), [original, edited]);
  const role = matrix?.role;
  const readOnly = !can('platform.roles', 'update') || !!role?.isSystem || !!role?.isFullAccess || saving;
  const productName = products.find((p) => p.code === product)?.displayName ?? product;
  const labelOf = useCallback((n: MatrixNode) => t(`menu.${n.label}`, { defaultValue: titles[n.resourceId] || n.label }), [t, titles]);

  const applyMatrix = (data: PermissionMatrix, keepCollapsed = false) => {
    setMatrix(data);
    const grants = grantsFor(data.tree);
    setOriginal(grants); setEdited(grants);
    if (!keepCollapsed) setCollapsed(data.tree.length > 12 ? new Set(data.tree.map((r) => r.resourceId)) : new Set());
  };

  // Products the admin may manage + roles of the organization.
  useEffect(() => {
    if (!canRead) return;
    let active = true;
    Promise.all([permissionApi.products(), authorizationApi.roles()]).then(([p, r]) => {
      if (!active) return;
      setProducts(p); setRoles(r);
      setProduct((current) => (p.some((x) => x.code === current) ? current : p[0]?.code ?? ''));
      setRoleId((current) => (r.some((x) => x.id === current) ? current : r[0]?.id ?? ''));
    }).catch((e) => { if (active) setError((e as Error).message); });
    return () => { active = false; };
  }, [canRead]);

  // Matrix for the selected role + product. The URL follows the selection so links can be shared.
  useEffect(() => {
    if (!roleId || !product || !canRead) return;
    const sequence = ++loadSequence.current;
    setLoading(true); setError(''); setNotice(''); setMatrix(null); setOriginal({}); setEdited({});
    permissionApi.matrix(roleId, product)
      .then((data) => { if (sequence === loadSequence.current) applyMatrix(data); })
      .catch((e) => { if (sequence === loadSequence.current) setError((e as Error).message); })
      .finally(() => { if (sequence === loadSequence.current) setLoading(false); });
    writePreference(product);
    setParams({ role: roleId, product: product.toLowerCase() }, { replace: true });
    return () => { loadSequence.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId, product, canRead]);

  // Guard: leaving the page (tab close or in-app link) with unsaved changes.
  useEffect(() => {
    if (!changes.length) return;
    const beforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    const onLink = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor || anchor.target === '_blank' || e.ctrlKey || e.metaKey || !anchor.href || anchor.href === window.location.href) return;
      e.preventDefault(); e.stopPropagation();
      const url = new URL(anchor.href);
      setPending(() => () => (url.origin === window.location.origin ? navigate(url.pathname + url.search) : window.location.assign(url.href)));
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', onLink, true);
    return () => { window.removeEventListener('beforeunload', beforeUnload); document.removeEventListener('click', onLink, true); };
  }, [changes.length, navigate]);

  async function save(): Promise<boolean> {
    if (!matrix || readOnly || !changes.length) return false;
    setSaving(true); setError(''); setNotice('');
    try {
      await permissionApi.save(roleId, matrix.version, toSaveBody(product, nodes, edited));
      applyMatrix(await permissionApi.matrix(roleId, product), true);
      setNotice(t('permissions.saved', { defaultValue: 'Saved. Members get the new permissions the next time their session refreshes (within 10 minutes).' }));
      return true;
    } catch (e) {
      if (e instanceof PlatformApiError && e.code === 'version_conflict') {
        // Someone saved first: reload, then re-apply our local changes on top for review.
        const local = diff(original, edited);
        try {
          const refreshed = await permissionApi.matrix(roleId, product);
          const baseline = grantsFor(refreshed.tree);
          const ids = new Set(flatten(nodesFor(refreshed.tree)).map((n) => n.resourceId));
          setMatrix(refreshed); setOriginal(baseline);
          setEdited({ ...baseline, ...Object.fromEntries(local.filter((c) => ids.has(c.resourceId)).map((c) => [c.resourceId, toMask(c.after)])) });
          setError(t('permissions.conflict', { defaultValue: 'Another administrator saved first. Your changes were reapplied. Review them and save again.' }));
        } catch (reloadError) { setError((reloadError as Error).message); }
      } else setError((e as Error).message);
      return false;
    } finally { setSaving(false); }
  }

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); void save(); }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });

  const switchTo = (action: () => void) => { if (changes.length) setPending(() => action); else action(); };

  // Search + "Show" filter. A matching submenu keeps its root visible.
  const matches = (n: MatrixNode) => `${labelOf(n)} ${n.label} ${n.code} ${n.url ?? ''} ${n.scopeBase ?? ''}`.toLowerCase().includes(search.trim().toLowerCase());
  const passes = (n: MatrixNode) => {
    const mask = expanded.state[n.resourceId] ?? 0;
    if (filter === 'granted') return !!role?.isFullAccess || mask !== 0;
    if (filter === 'ungranted') return !role?.isFullAccess && mask === 0;
    if (filter === 'changed') return (original[n.resourceId] ?? 0) !== (edited[n.resourceId] ?? 0);
    return true;
  };
  const narrowing = !!search.trim() || filter !== 'all';
  const visibleRoots: MatrixNode[] = nodes.flatMap((root) => {
    const kids = root.children.filter((c) => matches(c) && passes(c));
    const self = matches(root) && passes(root);
    if (!self && kids.length === 0) return [];
    return [{ ...root, children: narrowing ? kids : root.children }];
  });
  const shownCollapsed = narrowing ? new Set<string>() : collapsed;

  const allNodes = flatten(nodes);
  const grantedMenus = role?.isFullAccess ? allNodes.length : allNodes.filter((n) => (expanded.state[n.resourceId] ?? 0) !== 0).length;

  // ---------- roles ----------
  async function submitRole(form: RoleForm) {
    if (!roleDialog) return;
    setSaving(true); setDialogError('');
    try {
      const input = { ...form, code: form.code.trim(), name: form.name.trim(), copyFromRoleId: form.copyFromRoleId || undefined };
      if (roleDialog.editing) {
        await authorizationApi.updateRole(roleId, input);
        setRoles(await authorizationApi.roles());
        applyMatrix(await permissionApi.matrix(roleId, product), true);
      } else {
        const created = await authorizationApi.createRole(input);
        setRoles(await authorizationApi.roles());
        setRoleId(created.id);
      }
      setRoleDialog(null);
    } catch (e) { setDialogError((e as Error).message); } finally { setSaving(false); }
  }

  // ---------- resources ----------
  const newResource = (): ResourceInput => ({
    productCode: product, parentId: null, code: `${product.toLowerCase()}.`, label: '', title: '', url: '/', icon: '', sortOrder: 100,
    visibleInMenu: true, scopeBase: `${product.toLowerCase()}.`, supported: ['read', 'create', 'update', 'delete'], description: '', grantToRoleIds: [], readAlso: [],
  });

  async function openEditResource(node: MatrixNode) {
    setError('');
    try {
      const r = await permissionApi.resource(node.resourceId);
      setDialogError('');
      setResourceDialog({ id: node.resourceId, initial: {
        productCode: r.productCode, parentId: r.parentId, code: r.code, label: r.label, title: r.title, url: r.url ?? '',
        icon: r.icon ?? '', sortOrder: r.sortOrder ?? 0, visibleInMenu: !r.isHidden, scopeBase: r.scopeBase ?? '',
        supported: toNames(r.supportedActions), description: r.description ?? '', grantToRoleIds: [],
        readAlso: (r.readAlsoScopes ?? '').split(/[\s,;]+/).filter(Boolean),
      } });
    } catch (e) { setError((e as Error).message); }
  }

  async function submitResource(input: ResourceInput) {
    if (!resourceDialog) return;
    input = { ...input, readAlso: input.readAlso.filter(Boolean) };
    setSaving(true); setDialogError('');
    try {
      if (resourceDialog.id) await permissionApi.updateResource(resourceDialog.id, input);
      else await permissionApi.createResource({ ...input, title: input.title || input.label });
      setResourceDialog(null);
      const [data, p] = await Promise.all([permissionApi.matrix(roleId, product), permissionApi.products()]);
      applyMatrix(data, true); setProducts(p);
      setNotice(resourceDialog.id
        ? t('permissions.resourceSaved', { defaultValue: 'Resource saved.' })
        : input.visibleInMenu
          ? t('permissions.resourceAdded', { defaultValue: '“{{label}}” added.', label: input.title || input.label })
          : t('permissions.resourceAddedOpen', { defaultValue: '“{{label}}” added as an open resource: every member can use it.', label: input.title || input.label }));
    } catch (e) { setDialogError((e as Error).message); } finally { setSaving(false); }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setSaving(true); setError('');
    try {
      if (deleteTarget.kind === 'role') {
        await authorizationApi.deleteRole(deleteTarget.id);
        const updated = await authorizationApi.roles();
        setRoles(updated); setMatrix(null); setRoleId(updated[0]?.id ?? '');
      } else {
        await permissionApi.deleteResource(deleteTarget.id);
        applyMatrix(await permissionApi.matrix(roleId, product), true);
      }
      setDeleteTarget(null);
    } catch (e) { setDeleteTarget(null); setError((e as Error).message); } finally { setSaving(false); }
  }

  async function openCheckUser() {
    setCheckUser(true);
    if (members.length) return;
    try { setMembers(await platformApi.memberships()); } catch (e) { setError((e as Error).message); }
  }

  if (!canRead) {
    return <Alert severity="warning">{t('permissions.noAccess', { defaultValue: 'You do not have permission to view roles.' })}</Alert>;
  }

  const selectedRole = roles.find((r) => r.id === roleId);
  const presetLabel: Record<Preset, string> = {
    readOnly: t('permissions.presets.readOnly', { defaultValue: 'Read only' }),
    allButExecute: t('permissions.presets.allButExecute', { defaultValue: 'Everything except Execute' }),
    full: t('permissions.presets.full', { defaultValue: 'Full (incl. Execute)' }),
    clear: t('permissions.presets.clear', { defaultValue: 'Clear' }),
  };

  return (
    <div className="perm-page">
      <header className="perm-top">
        <div>
          <h1>{t('permissions.title', { defaultValue: 'Roles & Permissions' })}</h1>
          <p className="perm-sub">{t('permissions.subtitle', { defaultValue: 'Choose a role, then what it can do in each menu. Anything not listed here is open to everyone.' })}</p>
        </div>
        {/* Company provisioning still lives on the legacy Role Actions screen (SuperAdmin only). */}
        {hasScope('platform.organization.provision') && (
          <button type="button" className="perm-btn primary" onClick={() => switchTo(() => navigate('/role-actions/legacy'))}>
            + {t('permissions.newCompany', { defaultValue: 'New company' })}
          </button>
        )}
        <ProductTabs products={products} value={product} grantedInCurrent={matrix ? grantedMenus : null} disabled={saving}
          onChange={(code) => switchTo(() => setProduct(code))} />
      </header>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {!products.length && !error && !loading && roles.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('permissions.noProducts', { defaultValue: 'No products are available for you to manage.' })}</Alert>
      )}

      <div className="perm-layout">
        <RoleList roles={roles} selectedId={roleId} dirtyRoleId={changes.length ? roleId : null} canCreate={can('platform.roles', 'create')}
          disabled={saving} onSelect={(id) => switchTo(() => setRoleId(id))}
          onCreate={() => { setDialogError(''); setRoleDialog({ editing: false, initial: EMPTY_ROLE }); }} />

        <section className="perm-card perm-editor" aria-busy={loading}>
          {!roleId && !loading && <div className="perm-empty">{t('permissions.noRoles', { defaultValue: 'Create a role to start assigning permissions.' })}</div>}
          {loading && <div className="perm-empty"><CircularProgress size={28} aria-label={t('permissions.loading', { defaultValue: 'Loading permissions' })} /></div>}
          {matrix && role && (
            <>
              <div className="perm-head">
                <span className="perm-avatar lg" style={{ background: roleColor(role.code) }}>{initials(role.name)}</span>
                <div className="perm-head-text">
                  <h2>{role.name} <span className="perm-code">{role.code}</span></h2>
                  <p>{[role.description, t('permissions.memberCount', { count: selectedRole?.memberCount ?? 0, defaultValue: '{{count}} members' })].filter(Boolean).join(' · ')}</p>
                </div>
                <div className="perm-head-actions">
                  {can('platform.roles', 'update') && !role.isSystem && !role.isFullAccess && (
                    <button type="button" className="perm-btn ghost" disabled={saving}
                      onClick={() => { setDialogError(''); setRoleDialog({ editing: true, initial: { code: role.code, name: role.name, description: role.description ?? '', copyFromRoleId: '' } }); }}>
                      {t('permissions.editRole', { defaultValue: 'Edit role' })}
                    </button>
                  )}
                  {can('platform.roles', 'delete') && !role.isSystem && !role.isFullAccess && (
                    <button type="button" className="perm-btn ghost danger" disabled={saving}
                      onClick={() => switchTo(() => setDeleteTarget({ kind: 'role', id: roleId, name: role.name }))}>
                      {t('permissions.deleteRole', { defaultValue: 'Delete role' })}
                    </button>
                  )}
                  <button type="button" className="perm-btn" onClick={() => void openCheckUser()}>{t('permissions.checkUser', { defaultValue: 'Check a user' })}</button>
                </div>
              </div>

              {role.isFullAccess && (
                <div className="perm-banner full" role="note">
                  ✓ {role.code === 'ADMIN'
                    ? t('permissions.businessAdminBanner', { defaultValue: '{{role}} is the built-in business administrator: every {{product}} menu the platform has enabled for this organization. It cannot be modified; create a new role from it to customize access.', role: role.name, product: productName })
                    : t('permissions.fullBanner', { defaultValue: '{{role}} has full access to every {{product}} menu, including menus added later. Nothing to edit.', role: role.name, product: productName })}
                </div>
              )}
              {role.isSystem && !role.isFullAccess && (
                <div className="perm-banner info" role="note">
                  {t('permissions.systemBanner', { defaultValue: 'System roles are read-only. Create a new role from this one to customize it.' })}
                </div>
              )}

              <div className="perm-toolbar">
                <label className="perm-search">
                  <SearchIcon />
                  <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('permissions.searchMenus', { defaultValue: 'Search menu, URL or scope' })}
                    aria-label={t('permissions.searchMenus', { defaultValue: 'Search menu, URL or scope' })} />
                </label>
                <div className="perm-seg" role="group" aria-label={t('permissions.show', { defaultValue: 'Show' })}>
                  {(['all', 'granted', 'ungranted', 'changed'] as MatrixFilter[]).map((f) => (
                    <button key={f} type="button" aria-pressed={filter === f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
                      {t(`permissions.filter.${f}`, { defaultValue: { all: 'All', granted: 'Granted', ungranted: 'Not granted', changed: 'Changed' }[f] })}
                    </button>
                  ))}
                </div>
                <span className="perm-spacer" />
                {!narrowing && nodes.some((n) => n.children.length) && (
                  <button type="button" className="perm-btn ghost" onClick={() => setCollapsed(collapsed.size ? new Set() : new Set(nodes.filter((n) => n.children.length).map((n) => n.resourceId)))}>
                    {collapsed.size ? t('permissions.expandAll', { defaultValue: 'Expand all' }) : t('permissions.collapseAll', { defaultValue: 'Collapse all' })}
                  </button>
                )}
                <button type="button" className="perm-btn" disabled={readOnly} onClick={(e) => setQuickAnchor(e.currentTarget)}>
                  {t('permissions.quickSet', { defaultValue: 'Quick set' })} ▾
                </button>
                {canManageResources && (
                  <button type="button" className="perm-btn primary" disabled={saving}
                    onClick={() => switchTo(() => { setDialogError(''); setResourceDialog({ id: null, initial: newResource() }); })}>
                    + {t('permissions.addResource', { defaultValue: 'Add resource' })}
                  </button>
                )}
              </div>

              <PermissionMatrixTable
                roots={visibleRoots}
                state={expanded.state}
                requiredBy={expanded.requiredBy}
                original={original}
                edited={edited}
                readOnly={readOnly}
                fullAccess={role.isFullAccess}
                collapsed={shownCollapsed}
                canManageResources={canManageResources}
                labelOf={labelOf}
                onToggleCollapse={(id) => setCollapsed((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; })}
                onCell={(node, action) => setEdited((current) => applyCell(node, action, current))}
                onColumn={(action) => setEdited((current) => applyColumn(visibleRoots, action, current))}
                onRowPreset={(node, p) => setEdited((current) => applyPreset([node], p, current))}
                onEditResource={(node) => switchTo(() => void openEditResource(node))}
                onDeleteResource={(node) => switchTo(() => setDeleteTarget({ kind: 'resource', id: node.resourceId, name: labelOf(node) }))}
              />

              <div className="perm-banner info open" role="note">
                ⓘ {matrix.openScopesEnabled
                  ? t('permissions.openNote', { count: matrix.openResourceCount, product: productName, defaultValue: '{{count}} hidden {{product}} resources (APIs, actions, hidden menus) are not listed and are open to every member.' })
                  : t('permissions.openPending', { count: matrix.openResourceCount, product: productName, defaultValue: '{{count}} hidden {{product}} resources are not listed. Opening them to every member is switched off until the catalog check passes.' })}
              </div>

              <div className="perm-legend" aria-hidden="true">
                <span><span className="perm-chk static on"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="m3.5 8.5 3 3 6-7" /></svg></span>{t('permissions.legend.allowed', { defaultValue: 'Allowed' })}</span>
                <span><span className="perm-chk static partial"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M4 8h8" /></svg></span>{t('permissions.legend.partial', { defaultValue: 'Some submenus' })}</span>
                <span><span className="perm-na">—</span>{t('permissions.legend.unsupported', { defaultValue: 'Not available on this menu' })}</span>
                <span><span className="perm-chk static locked" />{t('permissions.legend.locked', { defaultValue: "You can't grant this" })}</span>
                <span><span className="perm-chk static dep" />{t('permissions.legend.dependency', { defaultValue: 'Required by another menu' })}</span>
                <span className="perm-legend-note">{t('permissions.legend.execute', { defaultValue: 'Execute = all actions + special commands (close day, print, fiscalize)' })}</span>
              </div>
            </>
          )}
        </section>
      </div>

      <div className={`perm-savebar ${changes.length ? 'show' : ''}`} aria-hidden={!changes.length}>
        <div className="perm-savebar-in">
          <span className="perm-dot" />
          <b>{t('permissions.changes', { count: changes.length, defaultValue: '{{count}} menus changed' })}</b>
          <span className="perm-hint">{changes.slice(0, 3).map((c) => titles[c.resourceId] ?? '').filter(Boolean).join(', ')}{changes.length > 3 ? '…' : ''}</span>
          <span className="perm-spacer" />
          <button type="button" className="perm-btn ghost" disabled={saving || !changes.length} onClick={() => setEdited(original)}>
            {t('permissions.discard', { defaultValue: 'Discard' })}
          </button>
          <button type="button" className="perm-btn primary" disabled={saving || readOnly || !changes.length} onClick={() => void save()}>
            {saving ? t('permissions.saving', { defaultValue: 'Saving…' }) : t('permissions.save', { defaultValue: 'Save' })}
          </button>
        </div>
      </div>

      <Menu anchorEl={quickAnchor} open={!!quickAnchor} onClose={() => setQuickAnchor(null)}>
        {(Object.keys(presetLabel) as Preset[]).map((p) => (
          <MenuItem key={p} onClick={() => { setEdited((current) => applyPreset(visibleRoots, p, current)); setQuickAnchor(null); }}>
            {presetLabel[p]}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={!!pending} onClose={() => !saving && setPending(null)}>
        <DialogTitle>{t('permissions.unsavedTitle', { defaultValue: 'Unsaved changes' })}</DialogTitle>
        <DialogContent>{t('permissions.unsavedBody', { count: changes.length, defaultValue: 'You changed {{count}} menus. Save them before you continue?' })}</DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setPending(null)}>{t('permissions.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button disabled={saving} color="error" onClick={() => { const action = pending; setPending(null); setEdited(original); action?.(); }}>
            {t('permissions.discard', { defaultValue: 'Discard' })}
          </Button>
          <Button disabled={saving} variant="contained" onClick={async () => { if (await save()) { const action = pending; setPending(null); action?.(); } }}>
            {t('permissions.save', { defaultValue: 'Save' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => !saving && setDeleteTarget(null)}>
        <DialogTitle>{t('permissions.deleteTitle', { defaultValue: 'Delete “{{name}}”?', name: deleteTarget?.name })}</DialogTitle>
        <DialogContent>
          {deleteTarget?.kind === 'role'
            ? t('permissions.deleteRoleBody', { defaultValue: 'A role that is still assigned to members cannot be deleted. Move its members to another role first.' })
            : t('permissions.deleteResourceBody', { defaultValue: 'The menu is deactivated for every organization. Menus with active submenus or dependencies cannot be deleted.' })}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setDeleteTarget(null)}>{t('permissions.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button color="error" variant="contained" disabled={saving} onClick={() => void confirmDelete()}>{t('permissions.delete', { defaultValue: 'Delete' })}</Button>
        </DialogActions>
      </Dialog>

      <RoleDialog open={!!roleDialog} editing={!!roleDialog?.editing} initial={roleDialog?.initial ?? EMPTY_ROLE} roles={roles}
        saving={saving} error={dialogError} onClose={() => setRoleDialog(null)} onSubmit={(f) => void submitRole(f)} />
      <ResourceDialog open={!!resourceDialog} editingId={resourceDialog?.id ?? null} initial={resourceDialog?.initial ?? null}
        productName={productName} roots={matrix?.tree ?? []} roles={roles} saving={saving} error={dialogError}
        onClose={() => setResourceDialog(null)} onSubmit={(input) => void submitResource(input)} />
      <EffectiveAccessDrawer open={checkUser} product={product} productName={productName} members={members} onClose={() => setCheckUser(false)} />
    </div>
  );
}
